import React, { useState, useEffect, useCallback } from "react";
import {
  getAsperoLogs,
  getAsperoLogsStats,
  getAsperoLogDetails,
  getAsperoLogsByUser,
  getAsperoLogsByEvent,
} from "../../services/api/aspero";
import UserActivityTimeline from "@/components/static/Admindashboard/AsperoComponents/UserActivityTimeline";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
];

const AsperoLogs = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    clientUuid: "",
    eventName: "",
    startDate: "",
    endDate: "",
    search: "",
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  
  // User timeline modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUserTimelineOpen, setIsUserTimelineOpen] = useState(false);

  // Fetch stats with error handling
  const fetchStats = useCallback(async () => {
    try {
      const response = await getAsperoLogsStats();

      if (response && response.data) {
        setStats(response.data.data);
      } else {
        setStats(null);
      }
    } catch (err) {
      console.error("Failed to fetch Aspero stats:", err);
      setStats(null);
    }
  }, []);

  // Fetch logs with comprehensive error handling
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Clean filters - remove empty values
      const cleanFilters = Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (value && value.trim() !== "") {
            acc[key] = value.trim();
          }
          return acc;
        },
        {}
      );

      const params = {
        page,
        limit,
        sort: sortBy,
        order: sortOrder,
        ...cleanFilters,
      };

      const response = await getAsperoLogs(params);

      // The API returns: { success: true, data: [...], meta: {...} }
      // But axios wraps it in response.data, so actual structure is:
      // response.data = { success: true, data: [...], meta: {...} }

      if (response && response.data && response.data.data) {
        const logsData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setLogs(logsData);
        setTotalPages(response.data.meta?.pages || 1);
        setTotal(response.data.meta?.total || 0);
      } else {
        setLogs([]);
        setTotalPages(1);
        setTotal(0);
      }
    } catch (err) {
      console.error("Failed to fetch Aspero logs:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch logs. Please try again."
      );
      setLogs([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters, sortBy, sortOrder]);

  // Initial load
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      clientUuid: "",
      eventName: "",
      startDate: "",
      endDate: "",
      search: "",
    });
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const handleRowClick = async (log) => {
    if (!log?._id) {
      console.error("Invalid log ID");
      return;
    }

    try {
      const response = await getAsperoLogDetails(log._id);

      if (response && response.data) {
        setSelectedLog(response.data);
        setIsModalOpen(true);
      } else {
        console.error("No data in response");
        alert("Failed to fetch log details");
      }
    } catch (err) {
      console.error("Failed to fetch log details:", err);
      alert(err?.response?.data?.message || "Failed to fetch log details");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLog(null);
  };

  const getEventBadgeColor = (eventName) => {
    if (!eventName) return "bg-gray-500";

    if (
      eventName.includes("SUCCESS") ||
      eventName.includes("VERIFIED") ||
      eventName.includes("COMPLETED")
    ) {
      return "bg-green-500";
    }
    if (eventName.includes("FAILED") || eventName.includes("REJECTED")) {
      return "bg-red-500";
    }
    if (
      eventName.includes("INITIATED") ||
      eventName.includes("STARTED") ||
      eventName.includes("SUBMITTED")
    ) {
      return "bg-blue-500";
    }
    if (eventName.includes("REVIEW")) {
      return "bg-yellow-500";
    }
    return "bg-gray-500";
  };

  const formatEventName = (eventName) => {
    if (!eventName) return "Unknown Event";
    return eventName.replace(/_/g, " ");
  };

  const renderStatsCards = () => {
    if (!stats) return null;

    const total = stats.totalWebhooks || 0;
    const eventCount = stats.eventDistribution?.length || 0;
    const today = stats.dailyStats?.[stats.dailyStats.length - 1]?.count || 0;
    const avg = stats.dailyStats?.length
      ? Math.round(
          stats.dailyStats.reduce((sum, day) => sum + (day.count || 0), 0) /
            stats.dailyStats.length
        )
      : 0;

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 p-4 rounded">
          <p className="text-sm text-gray-600 mb-1">Total Webhooks</p>
          <p className="text-2xl font-semibold text-gray-900">
            {total.toLocaleString()}
          </p>
        </div>

        <div className="bg-white border border-gray-200 p-4 rounded">
          <p className="text-sm text-gray-600 mb-1">Event Types</p>
          <p className="text-2xl font-semibold text-gray-900">{eventCount}</p>
        </div>

        <div className="bg-white border border-gray-200 p-4 rounded">
          <p className="text-sm text-gray-600 mb-1">Today's Events</p>
          <p className="text-2xl font-semibold text-gray-900">{today}</p>
        </div>

        <div className="bg-white border border-gray-200 p-4 rounded">
          <p className="text-sm text-gray-600 mb-1">Avg Daily</p>
          <p className="text-2xl font-semibold text-gray-900">{avg}</p>
        </div>
      </div>
    );
  };

  const renderCharts = () => {
    if (!stats) return null;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {stats.eventDistribution && stats.eventDistribution.length > 0 && (
          <div className="bg-white border border-gray-200 p-4 rounded">
            <h3 className="text-sm font-medium text-gray-800 mb-2">
              Events by Type
            </h3>
            <ul className="text-sm text-gray-700 divide-y divide-gray-100">
              {stats.eventDistribution.map((entry) => (
                <li
                  key={entry.eventName}
                  className="py-2 flex justify-between items-center"
                >
                  <span className="truncate max-w-xs">{entry.eventName}</span>
                  <span className="font-medium">{entry.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {stats.dailyStats && stats.dailyStats.length > 0 && (
          <div className="bg-white border border-gray-200 p-4 rounded">
            <h3 className="text-sm font-medium text-gray-800 mb-2">
              Recent Daily Counts
            </h3>
            <ul className="text-sm text-gray-700 divide-y divide-gray-100">
              {stats.dailyStats.slice(-7).map((day) => (
                <li
                  key={day.date || JSON.stringify(day)}
                  className="py-2 flex justify-between items-center"
                >
                  <span className="text-gray-600">{day.date || "—"}</span>
                  <span className="font-medium">{day.count || 0}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Aspero Webhook Logs
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor and analyze webhook events in real-time
          </p>
        </div>

        {renderStatsCards()}
        {renderCharts()}

        {/* Filters Section */}
        <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
          <h2 className="text-xl font-bold mb-4 text-gray-800">Filters</h2>
          <form onSubmit={handleFilterSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <input
                type="text"
                name="search"
                placeholder="Search (name, phone, event)..."
                value={filters.search}
                onChange={handleFilterChange}
                className="border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                name="clientUuid"
                placeholder="Client UUID"
                value={filters.clientUuid}
                onChange={handleFilterChange}
                className="border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                name="eventName"
                placeholder="Event Name (e.g., KYC_STARTED)"
                value={filters.eventName}
                onChange={handleFilterChange}
                className="border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="date"
                name="startDate"
                placeholder="Start Date"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="date"
                name="endDate"
                placeholder="End Date"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Apply Filters
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Clear Filters
              </button>
              <button
                type="button"
                onClick={fetchLogs}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                🔄 Refresh
              </button>
            </div>
          </form>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Webhook Logs</h2>
              <span className="text-sm text-gray-600">
                Showing {logs.length} of {total.toLocaleString()} results
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort("createdAt")}
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Timestamp{" "}
                    {sortBy === "createdAt" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client UUID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">
                          Loading logs...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="text-red-600">
                        <p className="text-lg font-medium">⚠️ Error</p>
                        <p className="text-sm mt-2">{error}</p>
                        <button
                          onClick={fetchLogs}
                          className="mt-4 bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
                        >
                          Try Again
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : logs && logs.length > 0 ? (
                  logs.map((log) => (
                    <tr
                      key={log._id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.createdAt
                          ? new Date(log.createdAt).toLocaleString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white ${getEventBadgeColor(
                            log.webhookData?.eventName
                          )}`}
                        >
                          {formatEventName(log.webhookData?.eventName)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>
                          <p className="font-medium">
                            {log.webhookData?.userDetails?.userName || "N/A"}
                          </p>
                          <p className="text-gray-500 text-xs">
                            {log.webhookData?.userDetails?.mobileNumber ||
                              "N/A"}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                        {log.webhookData?.userDetails?.clientUuid
                          ? log.webhookData.userDetails.clientUuid.slice(
                              0,
                              20
                            ) + "..."
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(log);
                          }}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View Details →
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUser({
                              clientUuid: log.webhookData?.userDetails?.clientUuid,
                              userName: log.webhookData?.userDetails?.userName || "User",
                            });
                            setIsUserTimelineOpen(true);
                          }}
                          className="text-green-600 hover:text-green-800 font-medium"
                        >
                          📊 Timeline
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <p className="text-lg font-medium">📭 No logs found</p>
                        <p className="text-sm mt-2">
                          Try adjusting your filters or check back later
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {logs.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(page - 1) * limit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(page * limit, total)}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium">
                      {total.toLocaleString()}
                    </span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                      className="relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  Webhook Log Details
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 overflow-auto max-h-[calc(90vh-140px)]">
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto text-sm font-mono">
                {JSON.stringify(selectedLog, null, 2)}
              </pre>
            </div>
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeModal}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Timeline Modal */}
      {isUserTimelineOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-200 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  User Activity Timeline
                </h2>
                <button
                  onClick={() => {
                    setIsUserTimelineOpen(false);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 overflow-auto max-h-[calc(95vh-100px)]">
              <UserActivityTimeline
                clientUuid={selectedUser.clientUuid}
                userName={selectedUser.userName}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AsperoLogs;
